import { motion } from 'framer-motion';
import { ArrowRight, Check, Eye, EyeOff, Headphones, ShieldCheck, Sparkles } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { post } from '../api';
import { useAuth } from '../App';
import Brand from '../components/Brand';
import type { User } from '../types';

export default function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const register = mode === 'register';

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setPending(true);
    const form = new FormData(event.currentTarget);
    try {
      const result = await post<{ user: User }>(`/auth/${mode}`, {
        name: form.get('name'), email: form.get('email'), password: form.get('password')
      });
      setUser(result.user);
      navigate('/dashboard', { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось продолжить');
    } finally {
      setPending(false);
    }
  };

  return (
    <motion.main className="auth-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <section className="auth-showcase">
        <Brand />
        <div className="auth-copy">
          <span className="live-label"><i /> Музыка движется</span>
          <h1>Продвижение<br />под контролем</h1>
          <p>Один кабинет для запуска кампаний, пополнения баланса и контроля каждого заказа.</p>
          <div className="auth-features">
            <span><Headphones size={18} /> Статус в реальном времени</span>
            <span><ShieldCheck size={18} /> Оплата по СБП</span>
            <span><Sparkles size={18} /> Все услуги в одном месте</span>
          </div>
        </div>
        <p className="auth-foot">HOLLA MARKET · 2026</p>
      </section>
      <section className="auth-form-side">
        <motion.div className="auth-form-wrap" initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.08 }}>
          <div className="auth-mobile-brand"><Brand /></div>
          <span className="eyebrow">Личный кабинет</span>
          <h2>{register ? 'Создать аккаунт' : 'С возвращением'}</h2>
          <p>{register ? 'Зарегистрируйтесь, чтобы оформить первый заказ.' : 'Войдите и продолжайте продвижение.'}</p>
          <form onSubmit={submit}>
            {register && <label>Имя или псевдоним<input name="name" autoComplete="name" placeholder="Например, ARGOMANIA" required minLength={2} /></label>}
            <label>Электронная почта<input name="email" type="email" autoComplete="email" placeholder="artist@example.com" required /></label>
            <label>Пароль<div className="password-field"><input name="password" type={showPassword ? 'text' : 'password'} autoComplete={register ? 'new-password' : 'current-password'} placeholder="Минимум 8 символов" required minLength={8} /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label="Показать пароль">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></label>
            {error && <div className="form-error">{error}</div>}
            <button className="primary-button full" disabled={pending}>{pending ? <span className="spinner" /> : <>{register ? 'Создать аккаунт' : 'Войти'}<ArrowRight size={18} /></>}</button>
          </form>
          <div className="auth-switch">
            <span>{register ? 'Уже есть аккаунт?' : 'Ещё нет аккаунта?'}</span>
            <Link to={register ? '/login' : '/register'}>{register ? 'Войти' : 'Зарегистрироваться'}</Link>
          </div>
          <div className="secure-note"><Check size={14} /> Данные защищены и не передаются третьим лицам</div>
        </motion.div>
      </section>
    </motion.main>
  );
}
